import React, { useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import type Canvas from 'diagram-js/lib/core/Canvas';
import { useDiagramContext } from '../Context/DiagramContext';
import { useDiagramDrag } from './UseDiagramDrag';
import { ZoomControls } from './DiagramZoomControl';
import { useDiagramZoom } from '../hooks/useDiagramZoom';
import { useDiagramLayout } from '../hooks/useDiagramLayout';

const DiagramCanvas: React.FC = () => {
  const {
    containerRef,
    bpmnModelerRef,
    loading,
    error,
    currentScale,
    setCurrentScale,
    isExpanded,
    isFitViewToggled,
    showAllAiComponents,
    setShowAllAiComponents,
  } = useDiagramContext();

  const { handleZoomIn, handleZoomOut, handleFitViewToggle, centerDiagram, handleInitialFitView } =
    useDiagramZoom();
  const { handleExpandToggle } = useDiagramLayout();

  const {
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useDiagramDrag({
    bpmnModelerRef,
    currentScale,
  });

  // Handle mouse wheel zoom
  const handleMouseWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      if (bpmnModelerRef.current) {
        const canvas = bpmnModelerRef.current.get('canvas') as Canvas;
        const delta = event.deltaY > 0 ? -0.05 : 0.05;
        const newScale = Math.min(Math.max(currentScale + delta, 0.2), 4);

        const rect = containerRef.current?.getBoundingClientRect();
        const mousePosition = {
          x: event.clientX - (rect?.left || 0),
          y: event.clientY - (rect?.top || 0),
        };

        canvas.zoom(newScale, mousePosition);
        setCurrentScale(newScale);

        if (showAllAiComponents) {
          setShowAllAiComponents(false);
          setTimeout(() => setShowAllAiComponents(true), 0);
        }
      }
    },
    [
      bpmnModelerRef,
      containerRef,
      currentScale,
      setCurrentScale,
      showAllAiComponents,
      setShowAllAiComponents,
    ]
  );

  // Attach wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleMouseWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleMouseWheel);
      };
    }
  }, [handleMouseWheel]);

  // Handle resize on expand toggle
  useEffect(() => {
    if (bpmnModelerRef.current) {
      const timer = setTimeout(() => {
        try {
          const canvas = bpmnModelerRef.current?.get('canvas') as Canvas;
          if (canvas) {
            canvas.resized();
            if (isExpanded) {
              centerDiagram();
            } else {
              handleInitialFitView();
            }
          }
        } catch (error) {
          console.error('Error resizing diagram:', error);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, centerDiagram, handleInitialFitView]);

  return (
    <Box
      sx={{
        position: 'relative',
        flexGrow: 1,
        minHeight: 0,
        height: 'auto',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'flex-start',
        gap: 2,
        backgroundColor: 'rgba(244, 246, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        mx: 'auto',
        borderRadius: { xs: 0, md: 2 },
        zIndex: 100,
        '& .bjs-container': {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          outline: 'none !important',
          '& *': {
            outline: 'none !important',
          },
          '& .djs-palette': {
            position: 'absolute',
            left: { xs: '8px', sm: '20px' },
            top: { xs: '8px', sm: '70px' },
            borderRadius: '8px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 100,
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            outline: 'none !important',
          },
          '& .djs-container': {
            border: 'none !important',
            outline: 'none !important',
          },
          '& .djs-canvas': {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            outline: 'none !important',
            '& svg': {
              width: '100% !important',
              height: '100% !important',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '100% !important',
              maxHeight: '100% !important',
              outline: 'none !important',
            },
          },
        },
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={24} />
          Loading Diagram...
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'error.main',
            zIndex: 1100,
          }}
        >
          Error: {error}
        </Box>
      )}

      <Box
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          flexGrow: 1,
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' },
          outline: 'none !important',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out',
        }}
      />

      {/* Zoom Controls */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 16, sm: 40 },
          left: { xs: 12, sm: 35 },
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'none',
          pointerEvents: 'auto',
        }}
      >
        <ZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitView={handleFitViewToggle}
          isFitViewToggled={isFitViewToggled}
          onExpandToggle={handleExpandToggle}
          isExpanded={isExpanded}
        />
      </Box>
    </Box>
  );
};

export default DiagramCanvas;
